// Shared helpers for enum-style API fields (verification method, direction, decision,
// subject type, ...). The API may send an enum as its raw value (number or name) and,
// on the newer responses, a readable `<field>Text` next to it. Always read through
// enumOf() so both shapes keep working.

// Readable value of `field` on `obj`: `<field>Text` when the API sends it, else the raw value.
export function enumOf(obj, field) {
  if (!obj) return undefined;
  for (const k of [`${field}Text`, `${field}Name`, `${field}Label`]) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  const raw = obj[field];
  return raw === null ? undefined : raw;
}

// "Primary Occupant" / "primary_occupant" / "PrimaryOccupant" -> "primaryoccupant"
export function enumKey(value) {
  if (value === undefined || value === null) return "";
  return String(value).toLowerCase().replace(/[^a-z0-9]/g, "");
}

// "PrimaryOccupant" -> "Primary Occupant"
export function splitWords(value) {
  if (value === undefined || value === null || value === "") return "";
  return String(value).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim();
}

const METHOD_BY_KEY = {
  "0": "Access Code", accesscode: "Access Code", code: "Access Code", manual: "Access Code",
  "1": "QR Code", qr: "QR Code", qrcode: "QR Code",
  "2": "Access Card", phone: "Phone Number", phonenumber: "Phone Number",
  "3": "Phone Number", nfc: "Access Card", card: "Access Card", accesscard: "Access Card",
  residentnfc: "Access Card", buildingnfc: "Access Card", nfccard: "Access Card",
};

export function methodLabel(value) {
  const key = enumKey(value);
  if (!key) return "—";
  if (METHOD_BY_KEY[key]) return METHOD_BY_KEY[key];
  if (key.includes("nfc") || key.includes("card")) return "Access Card";
  if (key.includes("qr")) return "QR Code";
  if (key.includes("phone")) return "Phone Number";
  if (key.includes("code")) return "Access Code";
  return splitWords(value);
}

export function methodColorClass(value) {
  switch (methodLabel(value)) {
    case "Access Card": return "text-success";
    case "QR Code": return "text-info";
    case "Access Code": return "text-primary";
    case "Phone Number": return "text-dark";
    default: return "text-gray-900";
  }
}

// Who a log/code belongs to: Tenant, PrimaryOccupant, Dependent, Visitor, ...
export function subjectTypeLabel(value) {
  const key = enumKey(value);
  if (!key) return "—";
  if (key === "tenant") return "Resident";
  if (key === "primaryoccupant") return "Primary Occupant";
  return splitWords(value);
}

// "In"/"Out" regardless of casing, numeric (0 = in, 1 = out) or "Entry"/"Exit" wording.
export function directionOf(value) {
  const key = enumKey(value);
  if (["in", "0", "entry", "entrance", "checkin"].includes(key)) return "In";
  if (["out", "1", "exit", "checkout"].includes(key)) return "Out";
  return "";
}

// Decision ("Allowed" / "Denied" ...) -> { label, tone: "allowed" | "denied" | "other" }.
export function decisionInfo(value) {
  const label = splitWords(value);
  if (!label) return { label: "—", tone: "other" };
  if (/allow|grant|success|approv/i.test(label)) return { label, tone: "allowed" };
  if (/den|reject|block|fail/i.test(label)) return { label, tone: "denied" };
  return { label, tone: "other" };
}

// ── Maintenance requests ─────────────────────────────────────────────
const MAINTENANCE_STATUS_BY_KEY = {
  "0": "Created", created: "Created", open: "Open", pending: "Created", new: "Created",
  "1": "In Progress", inprogress: "In Progress", assigned: "In Progress",
  "2": "Completed", completed: "Completed", resolved: "Completed", closed: "Completed",
  "3": "Canceled", canceled: "Canceled", cancelled: "Canceled", rejected: "Canceled",
};

// Status as the admin screens know it: Created / In Progress / Completed / Canceled.
export function maintenanceStatusLabel(value) {
  const key = enumKey(value);
  if (!key) return "";
  return MAINTENANCE_STATUS_BY_KEY[key] || splitWords(value);
}

// "Normal" / "Emergency" / "High" / "Low" — same spelling the API sends, spaces fixed.
export function maintenancePriorityLabel(value) {
  if (value === undefined || value === null || value === "") return "";
  const map = { "0": "Low", "1": "Normal", "2": "High", "3": "Emergency" };
  return map[String(value)] || splitWords(value);
}

// Resolves statusText / priorityLevelText on a maintenance request so pages can keep
// reading `status` and `priorityLevel`. Safe to call on a request that is already normalized.
export function normalizeMaintenanceRequest(req) {
  if (!req || typeof req !== "object") return req;
  const out = { ...req };
  const status = enumOf(req, "status");
  if (status !== undefined) out.status = maintenanceStatusLabel(status);
  const priority = enumOf(req, "priorityLevel") ?? enumOf(req, "priority");
  if (priority !== undefined) out.priorityLevel = maintenancePriorityLabel(priority);
  return out;
}

// Communication notes: senderType may come as senderTypeText ("Admin", "EstateAdmin", "Tenant").
export function normalizeCommunicationNote(note) {
  if (!note || typeof note !== "object") return note;
  const sender = enumOf(note, "senderType");
  return sender === undefined ? note : { ...note, senderType: String(sender).replace(/[\s_-]+/g, "") };
}

// Replaces each listed enum field with its readable `<field>Text` when the API sends one,
// without relabeling. For screens that already map the raw names themselves.
export function applyEnumText(obj, fields) {
  if (!obj || typeof obj !== "object") return obj;
  const out = { ...obj };
  fields.forEach((f) => {
    const v = enumOf(obj, f);
    if (v !== undefined) out[f] = v;
  });
  return out;
}

// ── Access codes & dependents ────────────────────────────────────────
const DURATION_UNITS = { "0": "Hours", "1": "Days", "2": "Weeks", "3": "Months" };

// durationUnit may arrive as a number or as durationUnitText ("Days").
export function durationUnitLabel(value) {
  if (value === undefined || value === null || value === "") return "";
  return DURATION_UNITS[String(value)] || splitWords(value);
}

// Dependent rows: dependentType (Staff / FamilyMember / Child / ...) via dependentTypeText.
export function normalizeDependent(dep) {
  return applyEnumText(dep, ["dependentType", "createdByType"]);
}

// Access-code rows: durationUnit, createdByType, dependentType and status via their *Text.
export function normalizeAccessCode(code) {
  if (!code || typeof code !== "object") return code;
  const out = applyEnumText(code, ["dependentType", "createdByType", "status", "accessCodeStatus"]);
  const unit = enumOf(code, "durationUnit");
  if (unit !== undefined) out.durationUnit = durationUnitLabel(unit);
  return out;
}

// ── Billing, invoices, payments, dashboards ──────────────────────────
const BILLING_ENUM_FIELDS = [
  "status", "invoiceStatus", "paymentStatus", "rentSchedule", "rentPaymentSchedule",
  "componentType", "componentCode", "priorityLevel", "subjectType", "accountType",
];

// Walks a response (object / array / nested) and swaps each billing enum field for its
// `<field>Text` where the API sends one. Covers invoices, payments, rent schedules,
// invoice components and the dashboard payloads in one pass. Returns a new structure.
export function applyBillingEnumText(data) {
  if (Array.isArray(data)) return data.map(applyBillingEnumText);
  if (!data || typeof data !== "object") return data;
  const out = {};
  Object.keys(data).forEach((k) => { out[k] = applyBillingEnumText(data[k]); });
  BILLING_ENUM_FIELDS.forEach((f) => {
    const text = data[`${f}Text`];
    if (text !== undefined && text !== null && text !== "") out[f] = text;
  });
  return out;
}

// account-activation-status rows: statusText / accountTypeText next to the raw values.
// `activationStatus` is what the screens read, so fill it from statusText when missing.
export function normalizeActivationAccount(acc) {
  if (!acc || typeof acc !== "object") return acc;
  const out = applyEnumText(acc, ["status", "accountType"]);
  if (!out.activationStatus && typeof out.status === "string") out.activationStatus = out.status;
  return out;
}

// How the person arrived at the gate: Vehicle / Walking / Unknown (checkInAccessMode).
export function accessModeLabel(value) {
  const key = enumKey(value);
  if (key === "vehicle" || key === "2") return "Vehicle";
  if (key === "walking" || key === "1") return "On foot";
  return "";
}
